import { yupResolver } from '@hookform/resolvers/yup';
import {
	Box,
	Button,
	FormControl,
	FormHelperText,
	Grid,
	InputAdornment,
	TextField,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from 'store/store';
import {
	activeUserAccount,
	loginUser,
	sentVerificationEmail,
} from 'store/user-service/asyncActions';
import { getUser } from 'store/user-service/selector';
import { removeLoginError } from 'store/user-service/userSlice';

import { ROUTES } from 'core/constants/routes';
import { NamespacesList } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';
import { loginSchema } from 'core/helpers/yupSchemas';
import { SignInType } from 'core/types/base';
import {
	getRemainingLockoutTime,
	isAccountLocked,
	recordFailedAttempt,
	resetLoginAttempts,
} from 'utils/localStorageHelpers/loginAttemptsHelper';

import { ErrorVerifyEmailModal } from 'pages/SignUp/ErrorVerifyEmailModal';

import { LoginHeader } from 'components/Header/LoginHeader';

import { ReactComponent as EyeSlash } from 'assets/icons/eye-slash.svg';
import { ReactComponent as Eye } from 'assets/icons/eye.svg';

import { SendVerifyLetterEmailModal } from './SendVerifyLetterModal';

export const Login = () => {
	const { t: logInPageTranslate } = useTranslation(NamespacesList['logInPage']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);
	const { t: modalsTranslate } = useTranslation(NamespacesList['modals']);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const userData = useSelector(getUser);

	const [showPassword, setShowPassword] = useState(false);
	const [openActivatingErrorVerify, setIsOpenActivatingErrorVerify] = useState(false);
	const [openSendingErrorVerify, setIsOpenSendingErrorVerify] = useState(false);
	const [openSendingVerify, setIsOpenSendingVerify] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const [userEmail, setUserEmail] = useState('');

	const {
		handleSubmit,
		register,
		formState: { errors },
		watch,
		getValues,
		reset,
	} = useForm({
		resolver: yupResolver(loginSchema()),
	});

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleActionActivatingError = () => {
		setIsOpenActivatingErrorVerify((prev) => !prev);
	};

	const handleActionSendingError = () => {
		setIsOpenSendingErrorVerify((prev) => !prev);
	};

	const handleActionSendingVerify = () => {
		setIsOpenSendingVerify((prev) => !prev);
	};

	const handleSentVerifyEmail = async () => {
		const emailToVerify = getValues().email || userEmail;
		if (emailToVerify) {
			const cleanedEmail = typeof emailToVerify === 'string' 
				? emailToVerify.trim().replace(/\s+/g, '').replace(/\u00A0/g, '') 
				: emailToVerify;
			!userEmail && setUserEmail(cleanedEmail);
			try {
				await dispatch(
					sentVerificationEmail({
						redirectLink: window.location.href,
						userEmail: cleanedEmail,
						successFunction: !openSendingVerify ? handleActionSendingVerify : undefined,
						errorFunction: handleActionSendingError,
					})
				).unwrap();
				// Si le statut est 200 OK, rediriger automatiquement vers /sign-in
				navigate(ROUTES.LOGIN);
				reset();
			} catch (error) {
				// L'erreur est déjà gérée par errorFunction
			}
		}
	};

	const activateYourAccountMessage = 'You need to activate your account';
	const incorrectLoginDataMessage = 'Login with these credentials has failed';

	const handleLoginError = async (error: any, email: string) => {
		// Gestion spéciale uniquement pour le compte non activé
		const errorData = error?.response?.data || error?.data || error;
		const errorStatus = error?.response?.status || error?.status;

		if (errorStatus === 403 && errorData === activateYourAccountMessage) {
			await handleSentVerifyEmail();
			errorNotify(
				modalsTranslate('isActivatedAccountError.title'),
				modalsTranslate('isActivatedAccountError.description')
			);
		} else {
			// Enregistrer la tentative échouée (sauf pour les erreurs d'activation)
			recordFailedAttempt(email);

			// Vérifier si le compte est maintenant bloqué
			if (isAccountLocked(email)) {
				const remainingTime = getRemainingLockoutTime(email);
				const minutes = Math.ceil(remainingTime / (60 * 1000));
				errorNotify(
					modalsTranslate('accountLockedError.title'),
					modalsTranslate('accountLockedError.description', { minutes })
				);
			} else {
				// Message générique pour toutes les autres erreurs de connexion (sécurité)
				errorNotify(
					modalsTranslate('incorrectLoginDataError.title'),
					logInPageTranslate('errors.loginFailed')
				);
			}
		}

		return error;
	};

	const handleLogin = async (data: SignInType['userData']) => {
		const email = data.email?.trim().toLowerCase() || '';

		// Vérifier si le compte est bloqué avant de tenter la connexion
		if (isAccountLocked(email)) {
			const remainingTime = getRemainingLockoutTime(email);
			const minutes = Math.ceil(remainingTime / (60 * 1000));
			errorNotify(
				modalsTranslate('accountLockedError.title'),
				modalsTranslate('accountLockedError.description', { minutes })
			);
			return;
		}

		try {
			const result = await dispatch(
				loginUser({
					userData: data,
				})
			).unwrap();

			if (result.status === 200) {
				// Réinitialiser les tentatives après une connexion réussie
				resetLoginAttempts(email);
				navigate(ROUTES.HOME);
			} else {
				await handleLoginError(result, email);
				dispatch(removeLoginError());
			}
		} catch (error: any) {
			// Gérer les erreurs rejetées par unwrap()
			await handleLoginError(error, email);
			dispatch(removeLoginError());
		}
	};

	useEffect(() => {
		dispatch(removeLoginError());
	}, [watch('email'), watch('password')]);

	const redirectToSignUp = () => {
		navigate(ROUTES.SIGNUP);
	};

	const redirectToResetPassword = () => {
		navigate(ROUTES.RESET_PASSWORD);
	};

	const hasLoginError = (): boolean => {
		const emailError = 'User with email';
		const passwordErrorOld = 'Login with this credentials was failed';
		const passwordErrorNew = 'Login with these credentials has failed';
		const activateAccountMessage = 'You need to activate your account';
		
		// Extraire le message d'erreur (peut être string ou objet)
		let errorMessage = '';
		if (typeof userData.loginError === 'string') {
			errorMessage = userData.loginError;
		} else if (userData.loginError && typeof userData.loginError === 'object') {
			const errorObj = userData.loginError as any;
			errorMessage = errorObj?.response?.data || errorObj?.data || '';
		}

		// Vérifier si c'est une erreur de connexion (sauf activation de compte)
		return !!(
			errorMessage &&
			typeof errorMessage === 'string' &&
			(errorMessage.startsWith(emailError) || 
			 errorMessage === passwordErrorOld || 
			 errorMessage === passwordErrorNew ||
			 errorMessage.includes('credentials') ||
			 errorMessage.includes('failed') ||
			 errorMessage.includes('not found')) &&
			!errorMessage.includes(activateAccountMessage)
		);
	};

	const renderEmailInfo = () => {
		const emailError = 'User with email';
		const passwordErrorOld = 'Login with this credentials was failed';
		const passwordErrorNew = 'Login with these credentials has failed';
		const activateAccountMessage = 'You need to activate your account';
		
		// Extraire le message d'erreur (peut être string ou objet)
		let errorMessage = '';
		if (typeof userData.loginError === 'string') {
			errorMessage = userData.loginError;
		} else if (userData.loginError && typeof userData.loginError === 'object') {
			const errorObj = userData.loginError as any;
			errorMessage = errorObj?.response?.data || errorObj?.data || '';
		}

		// Afficher un message générique pour toutes les erreurs de connexion (sécurité)
		// Sauf pour l'activation de compte qui a un traitement spécial
		if (
			errorMessage &&
			typeof errorMessage === 'string' &&
			(errorMessage.startsWith(emailError) || 
			 errorMessage === passwordErrorOld || 
			 errorMessage === passwordErrorNew ||
			 errorMessage.includes('credentials') ||
			 errorMessage.includes('failed') ||
			 errorMessage.includes('not found')) &&
			!errorMessage.includes(activateAccountMessage)
		) {
			return (
				<FormHelperText
					sx={{
						fontSize: { xs: '11px', sm: '12px' },
						textAlign: 'right',
						marginRight: '12px',
						marginTop: '0',
						color: '#FB0B24',
					}}
				>
					{logInPageTranslate('errors.loginFailed')}
				</FormHelperText>
			);
		} else {
			return (
				<FormHelperText
					sx={{
						fontSize: { xs: '11px', sm: '12px' },
						textAlign: 'right',
						marginRight: '12px',
						marginTop: '0',
					}}
				>
					{errors.email?.message}
				</FormHelperText>
			);
		}
	};

	const renderPasswordInfo = () => {
		const emailError = 'User with email';
		const passwordErrorOld = 'Login with this credentials was failed';
		const passwordErrorNew = 'Login with these credentials has failed';
		const activateAccountMessage = 'You need to activate your account';
		
		// Extraire le message d'erreur (peut être string ou objet)
		let errorMessage = '';
		if (typeof userData.loginError === 'string') {
			errorMessage = userData.loginError;
		} else if (userData.loginError && typeof userData.loginError === 'object') {
			const errorObj = userData.loginError as any;
			errorMessage = errorObj?.response?.data || errorObj?.data || '';
		}

		// Afficher un message générique pour toutes les erreurs de connexion (sécurité)
		// Sauf pour l'activation de compte qui a un traitement spécial
		if (
			errorMessage &&
			typeof errorMessage === 'string' &&
			(errorMessage.startsWith(emailError) || 
			 errorMessage === passwordErrorOld || 
			 errorMessage === passwordErrorNew ||
			 errorMessage.includes('credentials') ||
			 errorMessage.includes('failed') ||
			 errorMessage.includes('not found')) &&
			!errorMessage.includes(activateAccountMessage)
		) {
			return (
				<FormHelperText
					sx={{
						fontSize: { xs: '11px', sm: '12px' },
						textAlign: 'right',
						marginRight: '12px',
						marginTop: '0',
						color: '#FB0B24',
					}}
				>
					{logInPageTranslate('errors.loginFailed')}
				</FormHelperText>
			);
		}
		if (errors.password) {
			return (
				<FormHelperText
					sx={{
						fontSize: { xs: '11px', sm: '12px' },
						textAlign: 'right',
						marginRight: '12px',
						marginTop: '0',
					}}
				>
					{errors.password?.message}
				</FormHelperText>
			);
		}

		return null;
	};

	const handleActiveAccount = () => {
		if (searchParams.get('email') && searchParams.get('token')) {
			dispatch(
				activeUserAccount({
					token: searchParams.get('token') || '',
					userEmail: searchParams.get('email') || '',
					toastifyTitle: modalsTranslate('successVerifyEmail.title'),
					toastifyDescription: modalsTranslate('successVerifyEmail.description'),
					successFunction: () => {
						searchParams.delete('token');
						searchParams.delete('email');
						setSearchParams(searchParams);
					},
					errorFunction: handleActionActivatingError,
				})
			);
		}
	};

	useEffect(() => {
		handleActiveAccount();
	}, []);

	return (
		<>
			<LoginHeader />

			<Box
				className="rtl-able"
				sx={{
					display: 'flex',
					justifyContent: 'center',
					height: { xs: 'auto', md: 'calc(100vh - 164px)' },
					minHeight: { xs: 'calc(100vh - 164px)', md: 'auto' },
					alignItems: 'center',
					padding: { xs: '20px', sm: '24px', md: '0' },
				}}
			>
				<Grid container sx={{ width: { xs: '100%', sm: '374px' }, maxWidth: '374px', gap: '24px' }}>
					<Grid item sx={{ width: '100%', textAlign: 'center' }}>
						<Typography variant="h3" sx={{ fontSize: { xs: '28px', sm: '34px' }, fontWeight: '700' }}>
							{logInPageTranslate('text.logIn')}
						</Typography>
					</Grid>

					<Grid item>
						<form onSubmit={handleSubmit(handleLogin)}>
							<Grid container direction="column">
								<Grid item>
									<FormControl fullWidth error={!!errors.email || hasLoginError()}>
										<Typography sx={{ fontSize: { xs: '11px', sm: '12px' } }}>
											{logInPageTranslate('text.email')}
										</Typography>
										<TextField
											error={!!errors.email || hasLoginError()}
											inputProps={{ style: { padding: '10px 12px', fontSize: '16px' } }}
											placeholder="example@gmail.com"
											sx={{
												height: '44px',
												'& .MuiOutlinedInput-root': {
													borderRadius: '8px',
												},
											}}
											{...register('email')}
										/>
										{renderEmailInfo()}
									</FormControl>
								</Grid>

								<Grid item mt={2.4}>
									<FormControl fullWidth error={!!errors.password || hasLoginError()}>
										<Typography sx={{ fontSize: { xs: '11px', sm: '12px' } }}>
											{logInPageTranslate('text.password')}
										</Typography>
										<TextField
											error={!!errors.password || hasLoginError()}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<Button
															disableRipple
															sx={{
																minWidth: 0,
																'&:hover': {
																	backgroundColor: '#FFF',
																},
															}}
															onClick={handleClickShowPassword}
														>
															{showPassword ? <Eye /> : <EyeSlash />}
														</Button>
													</InputAdornment>
												),
											}}
											inputProps={{
												style: {
													padding: '10px 12px',
													fontSize: '16px',
												},
											}}
											sx={{
												height: '44px',
												'& .MuiOutlinedInput-root': {
													borderRadius: '8px',
												},
												'& input::-ms-reveal': {
													display: 'none !important',
												},
												'& input::-ms-clear': {
													display: 'none !important',
												}
											}}
											autoComplete="password"
											type={showPassword ? 'text' : 'password'}
											placeholder={logInPageTranslate('placeholders.password') as string}
											{...register('password')}
										/>
										{renderPasswordInfo()}
										<Typography
											sx={{
												fontSize: { xs: '11px', sm: '12px' },
												textAlign: 'right',
												marginRight: '12px',
												color: '#1A5EEC',
												cursor: 'pointer',
											}}
											onClick={redirectToResetPassword}
										>
											{logInPageTranslate('buttons.forgotPassword')}
										</Typography>
									</FormControl>
								</Grid>
							</Grid>

							{userData.loading ? (
								<Button
									sx={{
										marginTop: '24px',
										height: '44px',
										width: { xs: '100%', sm: '374px' },
										maxWidth: '374px',
										borderRadius: '8px',
									}}
									type="submit"
									variant="contained"
									color="primary"
								>
									<Oval
										height={24}
										width={24}
										color="#fff"
										wrapperStyle={{}}
										wrapperClass=""
										visible={true}
										ariaLabel="oval-loading"
										secondaryColor="#91B6FF"
										strokeWidth={5}
										strokeWidthSecondary={5}
									/>
								</Button>
							) : (
								<Button
									sx={{
										marginTop: '24px',
										height: '44px',
										width: { xs: '100%', sm: '374px' },
										maxWidth: '374px',
										borderRadius: '8px',
										textTransform: 'none',
										fontSize: { xs: '14px', sm: '16px' },
										fontWeight: '700',
									}}
									type="submit"
									variant="contained"
									color="primary"
								>
									{commonTranslate('buttons.logIn')}
								</Button>
							)}
						</form>
					</Grid>

					<Grid item sx={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
						<Typography sx={{ fontSize: { xs: '14px', sm: '16px' } }}>{logInPageTranslate('text.doNotHaveAccount')}</Typography>
						<Typography sx={{ color: '#4786FF', cursor: 'pointer', fontSize: { xs: '14px', sm: '16px' } }} onClick={redirectToSignUp}>
							{commonTranslate('buttons.signUp')}
						</Typography>
					</Grid>
				</Grid>
				<SendVerifyLetterEmailModal
					onClose={handleActionSendingVerify}
					isOpen={openSendingVerify}
					onConfirm={handleSentVerifyEmail}
				/>

				<ErrorVerifyEmailModal
					isOpen={openActivatingErrorVerify}
					onClose={handleActionActivatingError}
					onConfirm={() => {
						handleActiveAccount();
						handleActionActivatingError();
					}}
					description={modalsTranslate('errorVerifyEmail.activatingError') || ''}
				/>
				<ErrorVerifyEmailModal
					isOpen={openSendingErrorVerify}
					onConfirm={() => {
						handleSentVerifyEmail();
						handleActionSendingError();
					}}
					onClose={handleActionSendingError}
					description={modalsTranslate('errorVerifyEmail.sendingError') || ''}
				/>
			</Box>
		</>
	);
};
